import { useMemo } from "react";
import { useAuth } from "../context/AuthContext";

export function useConversationsApi() {
  const { apiFetch } = useAuth();

  return useMemo(
    () => ({
      // ISO em vez do toString() local: o backend interpreta sem depender do
      // formato de data do navegador.
      getAll: (date) =>
        apiFetch(
          `/conversations?date=${encodeURIComponent(new Date(date).toISOString())}`,
        ),

      getFilteredConversations: (filters) => {
        const params = new URLSearchParams();

        if (filters.search) params.append("search", filters.search);

        if (filters.name) params.append("name", filters.name);

        if (filters.phone) params.append("phone", filters.phone);

        if (filters.status) params.append("status", filters.status);

        if (filters.unread) params.append("unread", filters.unread);

        filters.users.forEach((id) => params.append("users", id));

        filters.tags.forEach((id) => params.append("tags", id));

        filters.steps.forEach((step) => params.append("steps", step));

        return apiFetch(`/conversations/filters?${params.toString()}`);
      },

      getById: (id) => apiFetch(`/conversations/${id}`),

      sendMessage: (data) =>
        apiFetch("/messages", {
          method: "POST",
          body: JSON.stringify(data),
        }),

      create: (data) =>
        apiFetch("/conversations", {
          method: "POST",
          body: JSON.stringify(data),
        }),

      update: (id, conversationData) =>
        apiFetch(`/conversations/${id}`, {
          method: "PUT",
          body: JSON.stringify(conversationData),
        }),

      summarize: (id, force = false) =>
        apiFetch(`/conversations/${id}/analyze${force ? "?force=true" : ""}`, {
          method: "PATCH",
        }),

      getAnalyses: (id) => apiFetch(`/conversations/${id}/analyses`),

      getTranscript: (id) => apiFetch(`/conversations/${id}/transcript`),

      getWaiting: () => apiFetch("/conversations/waiting"),

      getActive: (hours = 24) => apiFetch(`/conversations/active?hours=${hours}`),

      transferBulk: (conversationIds, userIds, mode = "replace") =>
        apiFetch("/conversations/transfer/bulk", {
          method: "PATCH",
          body: JSON.stringify({ conversationIds, userIds, mode }),
        }),

      search: (term, { limit = 20, offset = 0 } = {}) =>
        apiFetch(
          `/conversations/search?q=${encodeURIComponent(term)}&limit=${limit}&offset=${offset}`,
        ),

      setSlaState: (id, payload) =>
        apiFetch(`/conversations/${id}/sla`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        }),

      setSlaStateBulk: (conversationIds, payload) =>
        apiFetch("/conversations/sla/bulk", {
          method: "PATCH",
          body: JSON.stringify({ conversationIds, ...payload }),
        }),

      setAutoAnalysis: (id, enabled) =>
        apiFetch(`/conversations/${id}/auto-analysis`, {
          method: "PATCH",
          body: JSON.stringify({ enabled }),
        }),

      getAnalysesOverview: ({ days = 30, from, to } = {}) => {
        const params = new URLSearchParams();
        if (from) {
          params.append("from", from);
          if (to) params.append("to", to);
        } else {
          params.append("days", days);
        }
        return apiFetch(`/conversations/analyses/overview?${params.toString()}`);
      },

      delete: (id) =>
        apiFetch(`/conversations/${id}`, {
          method: "DELETE",
        }),

      markAsRead: (id) =>
        apiFetch(`/conversations/${id}`, {
          method: "PATCH",
        }),
    }),
    [apiFetch],
  );
}
